import { toast } from "@/lib/toast";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";
import api from "@/lib/api";

// R217 — Wallet top-up checkout. Mirrors lib/planCheckout.js exactly so the
// Cashfree behaviour (mock vs live modal vs redirect) stays consistent.

let _cfInstance = null;
async function getCashfree(mode) {
  if (_cfInstance) return _cfInstance;
  _cfInstance = await loadCashfree({ mode: mode === "live" ? "production" : "sandbox" });
  return _cfInstance;
}

/**
 * Top up the user's wallet by `amountPaise`. Resolves with the fresh wallet
 * object on success, or null if the user dropped out / it failed.
 */
export async function topUpWallet({ amountPaise } = {}) {
  const t = toast.loading("Preparing secure checkout…");
  try {
    const { data: order } = await api.post("/wallet/topup/order", { amount_paise: amountPaise });

    // Mock mode (dev / no Cashfree keys): verify directly.
    if (order.mock_mode) {
      await new Promise((r) => setTimeout(r, 600));
      const { data } = await api.post("/wallet/topup/verify", { topup_id: order.topup_id });
      toast.success(`₹${Math.round((order.credit_paise || amountPaise) / 100)} added to your wallet!`, { id: t });
      return data.wallet || null;
    }

    const cashfree = await getCashfree(order.mode);
    if (!cashfree || !order.payment_session_id) {
      toast.error("Failed to load payment gateway", { id: t });
      return null;
    }
    // Dismiss the loading toast BEFORE opening the Cashfree modal — otherwise
    // it lingers as "Preparing secure checkout…" the whole time the modal is up.
    toast.dismiss(t);
    const result = await cashfree.checkout({
      paymentSessionId: order.payment_session_id,
      redirectTarget: "_modal",
    });

    if (result?.error) {
      toast.error(result.error?.message || "Payment cancelled");
      return null;
    }
    if (result?.paymentDetails || result?.redirect === false) {
      try {
        const { data } = await api.post("/wallet/topup/verify", { topup_id: order.topup_id });
        toast.success(`₹${Math.round((order.credit_paise || amountPaise) / 100)} added to your wallet!`);
        return data.wallet || null;
      } catch (_e) {
        toast.error("Payment verification failed");
        return null;
      }
    }
    // Redirect flow — Cashfree return URL completes the journey.
    return null;
  } catch (err) {
    toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Could not start checkout", { id: t });
    return null;
  }
}
